import { createEffect, createEvent, createStore, sample } from 'effector';
import { $filesState, FileState, filesToState } from '@nx-frontend/shared/store/feature-files';
import { $orgPortalSettings } from '@nx-frontend/shared/store/org-portal-settings/org-portal-settings';
import { BucketFile } from '@nx-frontend/shared/types';
import { OrgPortalSettings } from '@nx-frontend/shared/types';
import { clientGetWorkspaceFiles } from '@nx-frontend/webnotes-client/shared/api';

export interface WorkspaceFiles {
  files: FileState;
  isFetching: boolean;
  firstLoad: boolean;
  loaded: boolean;
}

export interface WorkspaceFilesState {
  entities: Record<string, WorkspaceFiles>;
}

export const initialWorkspaceFilesState: WorkspaceFilesState = {
  entities: {},
};

export interface FetchWorkspaceFilesParams {
  workspaceId: string;
  showPortalFiles: boolean;
  portalId: string | undefined;
  limitFrom: number;
  limitSize: number;
}

export interface FetchWorkspaceFilesEffectParams extends FetchWorkspaceFilesParams {
  orgPortalSettings: OrgPortalSettings[];
}

export interface FetchWorkspaceFilesResponse {
  files: BucketFile[] | null;
  workspaceId: string;
  loaded: boolean;
}

export interface AddWorkspaceFilePayload {
  workspaceId: string;
  file: BucketFile;
}

export const $workspaceFiles = createStore(initialWorkspaceFilesState);

export const fetchWorkspaceFilesEvent = createEvent<FetchWorkspaceFilesParams>();
export const startFetchWorkspaceFilesEvent = createEvent<{ workspaceId: string }>();
export const addWorkspaceFileEvent = createEvent<AddWorkspaceFilePayload>();

export const fetchWorkspaceFilesEffect = createEffect(
  async (params: FetchWorkspaceFilesEffectParams): Promise<FetchWorkspaceFilesResponse> => {
    const limitSize = Math.ceil(params.limitSize / 2);
    const [files, prefetched] = await Promise.all([
      clientGetWorkspaceFiles({ ...params, limitSize }),
      // prefetch next page for be sure that all files are loaded
      clientGetWorkspaceFiles({ ...params, limitSize, limitFrom: params.limitFrom + limitSize }),
    ]);

    const portalSettingsHash: Record<string, OrgPortalSettings> = {};
    params.orgPortalSettings.forEach((setting) => {
      portalSettingsHash[setting.portalId] = setting;
    });

    const mappedFiles = [...(files || []), ...(prefetched || [])].map((file) => {
      if (file.breadcrumbs && file.breadcrumbs.length) {
        if (-1 === file.breadcrumbs[0].url?.indexOf('://')) {
          file.breadcrumbs[0].name = file.breadcrumbs[0].name ?? '';
          file.breadcrumbs[0].prefixLocKey = 'files_dashboard.files_dashboard-internal_space';
        } else if (file.portalId) {
          const name = file.breadcrumbs[0].name ?? 'Homepage';
          file.breadcrumbs[0].name = `${portalSettingsHash[file.portalId].settings.name} • ${name}`;
        }
      }
      return {
        ...file,
        createdAt: file.createdAt * 1000,
      };
    });

    return {
      files: mappedFiles,
      workspaceId: params.workspaceId,
      loaded: !files?.length || !prefetched?.length || prefetched.length < limitSize,
    };
  }
);

sample({
  source: {
    workspaceFiles: $workspaceFiles,
    orgPortalSettings: $orgPortalSettings,
  },
  clock: fetchWorkspaceFilesEvent,
  filter: ({ workspaceFiles }, { workspaceId }) => {
    return !workspaceFiles.entities[workspaceId]?.loaded && !workspaceFiles.entities[workspaceId]?.isFetching;
  },
  fn: ({ orgPortalSettings }, params) => ({ ...params, orgPortalSettings }),
  target: [startFetchWorkspaceFilesEvent, fetchWorkspaceFilesEffect],
});

$workspaceFiles.on(startFetchWorkspaceFilesEvent, (state, { workspaceId }) => {
  return {
    ...state,
    entities: {
      ...state.entities,
      [workspaceId]: {
        ...state.entities[workspaceId],
        isFetching: true,
      },
    },
  };
});

$workspaceFiles.on(
  fetchWorkspaceFilesEffect.doneData,
  (state, { files, workspaceId, loaded }: FetchWorkspaceFilesResponse) => {
    const filesState = filesToState(files || []);

    const workspaceFiles = state.entities[workspaceId]?.files || { entities: {}, ids: [] };
    const stateEntities = workspaceFiles.entities || {};
    const stateIds = workspaceFiles.ids || [];

    return {
      ...state,
      entities: {
        ...state.entities,
        [workspaceId]: {
          files: {
            entities: {
              ...stateEntities,
              ...filesState.entities,
            },
            ids: [...stateIds, ...filesState.ids],
          },
          isFetching: false,
          loaded: loaded,
          firstLoad: true,
        },
      },
    };
  }
);

const updateFileStoreEvent = createEvent<FileState>();

sample({
  source: $workspaceFiles,
  clock: fetchWorkspaceFilesEvent,
  filter: (_, { workspaceId }) => {
    return _.entities[workspaceId]?.loaded;
  },
  fn: (state, { workspaceId }) => {
    return state.entities[workspaceId].files;
  },
  target: updateFileStoreEvent,
});

$filesState.on(updateFileStoreEvent, (_, files) => {
  // TODO: временное решение пока на стороне бакет сервис не будет учитывать пермиссии для груп
  const items = files.ids.filter((id) => !files.entities[id].deleted).map((id) => files.entities[id]);
  const entities: Record<string, BucketFile> = {};
  items.forEach((item) => {
    entities[item.globalId] = item;
  });
  return {
    ..._,
    entities: {
      ..._.entities,
      ...entities,
    },
    ids: [..._.ids, ...Object.keys(entities)],
  };
});

sample({
  clock: fetchWorkspaceFilesEffect.doneData,
  fn: ({ files }) => {
    return filesToState(files || []);
  },
  target: updateFileStoreEvent,
});

export const updateAllFilesStoreEvent = createEvent<string>();
const setFilesStoreEvent = createEvent<FileState>();

sample({
  source: $workspaceFiles,
  clock: updateAllFilesStoreEvent,
  fn: (state, workspaceId) => {
    return state.entities[workspaceId]?.files ?? { entities: {}, ids: [] };
  },
  target: setFilesStoreEvent,
});

$filesState.on(setFilesStoreEvent, (_, files) => {
  // TODO: временное решение пока на стороне бакет сервис не будет учитывать пермиссии для груп
  const items = files.ids.filter((id) => !files.entities[id].deleted).map((id) => files.entities[id]);
  const entities: Record<string, BucketFile> = {};
  items.forEach((item) => {
    entities[item.globalId] = item;
  });

  return {
    ..._,
    entities,
    ids: Object.keys(entities),
  };
});

$workspaceFiles.on(addWorkspaceFileEvent, (state, { workspaceId, file }) => {
  const workspaceFiles = state.entities[workspaceId]?.files || { entities: {}, ids: [] };
  const stateEntities = workspaceFiles.entities || {};
  const stateIds = workspaceFiles.ids || [];

  return {
    ...state,
    entities: {
      ...state.entities,
      [workspaceId]: {
        ...state.entities[workspaceId],
        files: {
          entities: {
            ...stateEntities,
            [file.globalId]: file,
          },
          ids: [file.globalId, ...stateIds],
        },
      },
    },
  };
});

export function filesToWorkspaceFilesState(files: BucketFile[], workspaceId: string): WorkspaceFilesState {
  const state = filesToState(files);
  return {
    entities: {
      [workspaceId]: {
        files: state,
        isFetching: false,
        firstLoad: true,
        loaded: false,
      },
    },
  };
}
